/* eslint-disable no-param-reassign */
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import React, { useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { deleteForDonor, updateForDonor } from 'src/shared/services/donorService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useNavigate } from 'react-router-dom';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { DonorResponse, FormDonorPutItem } from '../models/donor';
import { DonorAutogiroMandatePicker } from './donorAutogiroMandatePicker';

type DonorEditModalProps = {
  modalOpen: boolean;
  onSave: (id?: number) => void;
  onClose: () => void;
  donor: DonorResponse;
};

export const DonorEditModal = (props: DonorEditModalProps) => {
  const { modalOpen, onSave, onClose, donor } = props;

  const { t } = useTranslation(['gift', 'common']);

  const [loading, setIsLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Make a reference to the top portion of the modal content
  // When an error occures, scroll top of modal into view
  const modalTop = useRef<HTMLDivElement>(null);
  const scrollToModalTop = () => modalTop.current && modalTop.current.scrollIntoView();

  const isMounted = useIsMounted();
  const navigate = useNavigate();

  const requiredMessage = t('common:validationMessages.required');
  const exactCharacterMessage = t('gift:createDonor.ssnValidation');

  const DonorRequestPostSchema: Schema<FormDonorPutItem> = yup.object().shape({
    id: yup.number().required(),
    name: yup.string().required(requiredMessage),
    autogiroMandate_id: yup.string().required(requiredMessage),
    personalIdentityNumber: yup.string().min(12, exactCharacterMessage).max(12, exactCharacterMessage).required(),
  });
  const formMethods = useForm({
    resolver: yupResolver(DonorRequestPostSchema),
    defaultValues: {
      id: donor?.id ?? 0,
      name: donor?.name ?? '',
      autogiroMandate_id: donor?.autogiroMandate_id ?? '',
      personalIdentityNumber: donor?.personalIdentityNumber ?? '',
    },
  });

  const { handleSubmit } = formMethods;

  const saveFormData = React.useCallback(
    async (data: FormDonorPutItem) => {
      const formData: FormDonorPutItem = {
        id: data.id,
        name: data.name,
        autogiroMandate_id: data.autogiroMandate_id,
        personalIdentityNumber: data.personalIdentityNumber,
      };
      ConsoleHelper.log('formData to put', formData);
      setIsLoading(true);
      try {
        const res = await updateForDonor(formData);
        if (isMounted()) {
          toast.success(<>{t('gift:editDonor.saveSuccess')}</>);
          setServerErrors([]);
          if (res.data) {
            setIsLoading(false);
            onSave(donor?.id);
          }
        }
      } catch (e: any) {
        if (isMounted()) {
          ConsoleHelper.log('saveFormDataError:', e);
          setIsLoading(false);
          setServerErrors(e.data);
          scrollToModalTop();
        }
      }
    },
    [donor?.id, isMounted, onSave, t],
  );

  const onSubmit = React.useCallback(
    (data: FormDonorPutItem) => {
      saveFormData(data);
    },
    [saveFormData],
  );

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const onHandleSave = React.useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const onDeleteDonor = async () => {
    setConfirmDeleteOpen(false);
    try {
      await deleteForDonor(donor?.id ?? 0);
      if (isMounted()) {
        onClose();
        navigate(RoutesEnum.Donors);
      }
    } catch (e: any) {
      if (isMounted()) {
        ConsoleHelper.log('ERR delete donor', e);
        toast.error(e.data);
      }
    }
  };

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={t('gift:editDonor.header')}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogContent={
        <Box
          ref={modalTop}
          sx={{ py: 2, position: 'relative', border: (theme) => (serverErrors.length > 0 ? `8px solid ${theme.palette.error.main}` : 'none') }}
        >
          <LoadingOverlay loading={loading} />

          <Box sx={{ p: 2 }}>
            <FormProvider {...formMethods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <GridWrapper>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='name'
                      required
                      label={t('gift:detailDonor.name')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='personalIdentityNumber'
                      required
                      type='number'
                      label={`${t('gift:detailDonor.personalIdentityNumber')} (YYYYMMDDXXXX)`}
                      inputProps={{ maxLength: 12 }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DonorAutogiroMandatePicker />
                  </Grid>
                </GridWrapper>
              </form>
            </FormProvider>
          </Box>
          {/* Dialog bekräfta radering av gåvogivare */}
          <Dialog maxWidth='xs' open={confirmDeleteOpen}>
            <DialogTitle>{t('gift:deleteDonor.header')}</DialogTitle>
            <DialogContent>
              <Typography>{t('gift:deleteDonor.deleteText')}</Typography>
            </DialogContent>
            <DialogActions>
              <Button autoFocus variant='outlined' onClick={() => setConfirmDeleteOpen(false)}>
                {t('common:button.cancel')}
              </Button>
              <Button variant='contained' color='primary' disableElevation onClick={() => onDeleteDonor()}>
                {t('common:button.ok')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      }
      dialogActions={
        <Box display='flex' justifyContent='space-between' sx={{ width: '100%' }}>
          <Button variant='outlined' color='primary' disableElevation onClick={() => setConfirmDeleteOpen(true)}>
            {t('common:button.delete')}
          </Button>
          <Box>
            <Button variant='outlined' color='primary' disableElevation onClick={onHandleClose} sx={{ mr: 1 }}>
              {t('common:button.cancel')}
            </Button>
            <Button autoFocus variant='contained' color='primary' disableElevation onClick={onHandleSave}>
              {t('common:button.save')}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};
